// Gallery.js
// import './Gallery.css';
import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Slider from 'react-slick';
import { fetchApi } from '../config/api';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import SeparatorLine from '../assets/photos/sep3.png';

const Gallery = () => {
  const { t } = useLanguage();
  useEffect(() => {
    const importCss = async () => {
      try {
        await import('./Gallery.css');//replacing the css with the css of the gallery of the suites
      } catch (err) {}
    };
    importCss();
  }, []);

  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeImages, setActiveImages] = useState([]);
  const [sliderOpen, setSliderOpen] = useState(false);
  const [photosReady, setPhotosReady] = useState(false);
  const thumbnailRefs = useRef([]);
  const sliderRef = useRef(null);

  const refreshSlider = () => {
    window.dispatchEvent(new Event('resize'));
    const inner = sliderRef.current && sliderRef.current.innerSlider;
    if (inner && typeof inner.onWindowResized === 'function') {
      inner.onWindowResized();
    }
  };

  const preloadImage = (url) =>
    new Promise((resolve) => {
      if (!url) {
        resolve();
        return;
      }
      const img = new Image();
      img.onload = () => {
        if (typeof img.decode === 'function') {
          img.decode().then(resolve).catch(resolve);
        } else {
          resolve();
        }
      };
      img.onerror = resolve;
      img.src = url;
    });

  const isIOS = () => {
    return [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform)
    || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
  }

  useEffect(() => {
    fetchApi('/api/imgcategory/').then(response => {
      setCategories(response.data);
      thumbnailRefs.current = response.data.map(() => React.createRef());
    }).catch((error) => {
      console.error('Failed to load gallery categories:', error);
    });
  }, []);

// In the second useEffect, observe the refs:
useEffect(() => {
  if (categories.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.style.filter = 'none';
        } else {
          entry.target.style.filter = 'grayscale(60%) sepia(40%)';
        }
      });
    },
    {
      threshold: 1,
    }
  );

  thumbnailRefs.current.forEach((ref) => {
    if (ref.current) {
      observer.observe(ref.current);
    }
  });

  return () => {
    thumbnailRefs.current.forEach((ref) => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    });
  };
}, [categories]);

useEffect(() => {
  if (!photosReady) return;
  const id = window.requestAnimationFrame(refreshSlider);
  return () => window.cancelAnimationFrame(id);
}, [photosReady]);

useEffect(() => {
  const goFullscreen = () => {
    const sliderContainer = document.querySelector('.slider-popup');
    
        if (sliderContainer && sliderContainer.requestFullscreen) {
          sliderContainer.requestFullscreen();
        } else if (sliderContainer && sliderContainer.mozRequestFullScreen) { /* Firefox */
            sliderContainer.mozRequestFullScreen();
        } else if (sliderContainer && sliderContainer.webkitRequestFullscreen) { /* Chrome, Safari and Opera */
            sliderContainer.webkitRequestFullscreen();
        } else if (sliderContainer && sliderContainer.msRequestFullscreen) { /* IE/Edge */
            sliderContainer.msRequestFullscreen();
        }

        // Attempt to lock the screen orientation to landscape
        if (window.screen.orientation && window.screen.orientation.lock) {
          window.screen.orientation.lock('landscape').catch((err) => {
            console.warn('Screen orientation lock failed:', err);
          });
        }
    // }
  };

  if (sliderOpen && activeImages.length > 0) {
    const onFullscreenChange = () => refreshSlider();
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    const id = window.requestAnimationFrame(() => {
      goFullscreen();
    });
    return () => {
      window.cancelAnimationFrame(id);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
      if (isIOS()) {
        const sliderContainer = document.querySelector('.slider-popup');
        if (sliderContainer) {
          sliderContainer.removeAttribute("style");
        }
      }
      exitFullscreen();
      if (window.screen.orientation && window.screen.orientation.unlock) {
        window.screen.orientation.unlock();
      }
    };
  }

  return () => {};
}, [sliderOpen, activeImages.length]);

const exitFullscreen = () => {
  if (document.fullscreenElement && document.exitFullscreen) {
    document.exitFullscreen();
  } else if (document.mozCancelFullScreen) {
    document.mozCancelFullScreen();
  } else if (document.webkitExitFullscreen) {
    document.webkitExitFullscreen();
  } else if (document.msExitFullscreen) {
    document.msExitFullscreen();
  }
};

const handleCategoryClick = (event, categoryName) => {
  if (isIOS() && event.type !== 'touchend') return;

  setActiveCategory(categoryName);

  fetchApi(`/api/imggallery/${categoryName}/`).then(async (response) => {
    const images = response.data || [];
    setPhotosReady(false);
    await preloadImage(images[0] && images[0].image);
    if (images[1] && images[1].image) {
      preloadImage(images[1].image);
    }
    setActiveImages(images);
    setSliderOpen(true);
    setPhotosReady(true);
  }).catch((error) => {
    console.error('Failed to load gallery images:', error);
  });
};

const [touchStart, setTouchStart] = useState({ x: null, y: null });

const handleTouchStart = (event) => {
  setTouchStart({
    x: event.touches[0].clientX,
    y: event.touches[0].clientY,
  });
};

const handleTouchEnd = (event, name) => {
  const touchEnd = {
    x: event.changedTouches[0].clientX,
    y: event.changedTouches[0].clientY,
  };

  if (
    Math.abs(touchStart.x - touchEnd.x) < 5 &&
    Math.abs(touchStart.y - touchEnd.y) < 5
  ) {
    handleCategoryClick(event, name);
  }
};
  
  const handleCloseSlider = () => {
    setSliderOpen(false);
    setActiveCategory(null);
    setActiveImages([]);
    setPhotosReady(false);
  };
  const [activeIndex, setActiveIndex] = useState(0);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    autoplay: false,
    autoplaySpeed: 4000,
    afterChange: (currentSlide) => {
      setActiveIndex(currentSlide);
    },
  };

  return (
    <div className="gallery-container" id="gallery-photos">
      <img src={SeparatorLine} className="separator" alt="Separator Line" />
      <h3>{t('suiteCards.title')}</h3>
      <div className="categories-grid">
        {categories.map((category, index) => (
          <div
            key={category.id}
            className="category-thumbnail"
            role="button"
            tabIndex="0"
            style={{ cursor: 'pointer' }}
            // onClick={() => handleCategoryClick(category.name)}
            onClick={(event) => handleCategoryClick(event, category.name)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleCategoryClick(event, category.name);
              }
            }}
            onTouchStart={isIOS() ? handleTouchStart : undefined}
            onTouchEnd={isIOS() ? (event) => handleTouchEnd(event, category.name) : undefined}
            // onTouchEnd={(event) => handleCategoryClick(event, category.name)} // For iOS touchend
          >
            <img 
              src={category.thumbnail} 
              alt={category.name} 
              loading="lazy"
              decoding="async"
              ref={thumbnailRefs.current[index]} // Attach the ref
              />           
            <p>{category.name}</p>
          </div>
        ))}
      </div>
      {sliderOpen && activeImages.length > 0 && (
        <div className={`slider-popup${photosReady ? ' photos-ready' : ''}`} id="slider-popup">
          <button className="close-button" onClick={handleCloseSlider}>X</button>
          <Slider ref={sliderRef} {...settings} >
            {activeImages.map((image, index) => (
              <div key={image.id}>
                <div className="slide-content">
                  <div className="image-container">
                    <img
                      src={image.image}
                      alt={image.title}
                      loading={index === 0 || index === 1 || index === activeImages.length - 1 ? 'eager' : 'lazy'}
                    />
                  </div>
                </div>
              </div>
            ))}
          </Slider>

        </div>
      )}
    </div>
  );
};

export default Gallery;
